import { MotionConfig } from 'framer-motion'
import { OpenImgContextProvider } from 'openimg/react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetcher,
	useLoaderData,
	useMatches,
} from 'react-router'
import { useChangeLanguage } from 'remix-i18next/react'
import {
	HoneypotInputs,
	HoneypotProvider,
} from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { AIAssistant } from './components/ai-assistant.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import VariableFontCursorProximity from './components/fancy/text/variable-font-cursor-proximity.tsx'
import { LanguageSwitcher } from './components/language-switcher-remix.tsx'
import { MobileNav } from './components/mobile-nav.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { SearchDrawer } from './components/search-drawer.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { SearchDrawerProvider } from './contexts/search-drawer-context.tsx'
import {
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import apfelgroteskStyleSheetUrl from './styles/apfelgrotezk.css?url'
import recoletaStyleSheetUrl from './styles/recoleta.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { getLocale } from './utils/locale.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getSiteSettings } from './utils/site-settings.server.ts'
import { getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload font stylesheets
		{ rel: 'preload', href: apfelgroteskStyleSheetUrl, as: 'style' },
		{ rel: 'preload', href: recoletaStyleSheetUrl, as: 'style' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: apfelgroteskStyleSheetUrl },
		{ rel: 'stylesheet', href: recoletaStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{
			title: data
				? 'Else Ringnalda — Beeldend Kunstenaar'
				: 'Fout | Else Ringnalda',
		},
		{
			name: 'description',
			content:
				'Else Ringnalda, beeldend kunstenaar. Figuratieve sculpturen in brons en steen. Beelden in publieke ruimte en collecties door heel Europa.',
		},
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							aiVoiceEnabled: true,
							aiVoiceModel: true,
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()
	const locale = await getLocale(request)
	// Two booleans (shop nav visibility, registration) — LRU-cached, ~free.
	const siteSettings = await getSiteSettings({ timings })

	return data(
		{
			user,
			siteSettings,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			locale,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const handle = { i18n: 'common' }

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	env = {},
	lang = 'nl',
	dir = 'ltr',
}: {
	children: React.ReactNode
	nonce: string
	env?: Record<string, string | undefined>
	lang?: string
	dir?: 'ltr' | 'rtl' | 'auto'
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang={lang} dir={dir} className="min-h-full overflow-x-hidden">
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				{/* Pass the nonce explicitly: without it <Links /> uses the
				    ServerRouter context nonce on the server only, while the
				    client (HydratedRouter has no nonce) renders none — a
				    hydration mismatch on every stylesheet link. With the prop,
				    both sides agree (browsers hide nonce values as "", which
				    equals the client's useNonce() fallback). */}
				<Links nonce={nonce} />
			</head>
			<body className="h-full overflow-x-hidden" style={{ background: 'var(--paper)', color: 'var(--ink)' }}>
				{/* Signature museum-mat frame */}
				<div
					className="pointer-events-none fixed inset-0 z-50"
					style={{ border: 'var(--frame-w) solid var(--paper)', }}
				/>

				{/* Main content */}
				<div className="h-full" style={{ padding: 'var(--frame-w)' }}>
					<div className="h-full">{children}</div>
				</div>
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} getKey={(location) => location.pathname} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const { i18n } = useTranslation()
	// Prefer the loader's locale: it is what the server resolved for this
	// request (SSR-consistent) and it updates immediately on revalidation,
	// while `i18n.language` only flips after useChangeLanguage's effect runs.
	// i18n.language remains the fallback for error states without loader data.
	const lang = data?.locale || i18n.language || 'nl'
	const dir = i18n.dir ? i18n.dir(lang) : 'ltr'
	return (
		<Document nonce={nonce} env={data?.ENV} lang={lang} dir={dir}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	const isOnSearchPage = matches.find((m) => m.id === 'routes/users+/index')
	const { t } = useTranslation()

	useToast(data.toast)
	useChangeLanguage(data.locale)

	return (
		<MotionConfig reducedMotion="user">
		<SearchDrawerProvider>
			<OpenImgContextProvider
				optimizerEndpoint="/resources/images"
				getSrc={getImgSrc}
			>
				<div className="flex min-h-screen flex-col">
					<header
						className="fixed z-40 flex h-[72px] items-center justify-between transition-all duration-[360ms] md:px-8"
						style={{
							top: 'var(--frame-w)',
							left: 'var(--frame-w)',
							right: 'var(--frame-w)',
							padding: '0 32px',
							background: 'rgba(250, 248, 244, 0.78)',
							backdropFilter: 'blur(14px)',
							WebkitBackdropFilter: 'blur(14px)',
							borderBottom: '1px solid transparent',
						}}
					>
						<Logo />

						<div className="flex items-center gap-2">
							<LanguageSwitcher />
							{!isOnSearchPage && <SearchDrawer />}
							<MobileNav
								links={[
									{ to: '/', label: t('home') },
									{ to: '/beelden', label: t('gallery') },
									{ to: '/exposities', label: t('exhibitions') },
									// Shop link only while the shop is enabled in
									// /admin/instellingen.
									...(data.siteSettings.shopEnabled
										? [{ to: '/shop', label: t('shop') }]
										: []),
									{ to: '/over', label: t('about') },
									{ to: '/contact', label: t('contact') },
								]}
								user={user}
								isAdmin={user?.roles.some((role) => role.name === 'admin')}
							/>
						</div>
					</header>

					<main className="flex flex-1 flex-col pt-[72px]" style={{ animationName: 'page-in', animationDuration: '600ms', animationTimingFunction: 'var(--ease)' }}>
						<Outlet />
					</main>

					<SiteFooter />
				</div>
				{user?.roles.some((role) => role.name === 'admin') && (
					<AIAssistant
						userName={user?.name || 'Else'}
						voiceEnabled={user?.aiVoiceEnabled ?? true}
						voiceModel={user?.aiVoiceModel ?? 'nova'}
					/>
				)}
				<EpicToaster closeButton position="top-center" theme={theme} />
				<EpicProgress />
			</OpenImgContextProvider>
		</SearchDrawerProvider>
		</MotionConfig>
	)
}

function Logo() {
	const containerRef = useRef<HTMLDivElement>(null)

	return (
		<div ref={containerRef}>
			<Link
				to="/"
				className="font-heading select-none text-[22px] tracking-tight"
				style={{ color: 'var(--ink)', letterSpacing: '-0.01em' }}
				onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
			>
				<VariableFontCursorProximity
					containerRef={containerRef}
					fromFontVariationSettings="'wght' 600, 'opsz' 48, 'WONK' 0"
					toFontVariationSettings="'wght' 900, 'opsz' 48, 'WONK' 0"
					radius={120}
					falloff="gaussian"
					className="font-heading"
				>
					Else
				</VariableFontCursorProximity>
				{' '}
				<VariableFontCursorProximity
					containerRef={containerRef}
					fromFontVariationSettings="'wght' 400, 'opsz' 48, 'WONK' 0"
					toFontVariationSettings="'wght' 800, 'opsz' 48, 'WONK' 0"
					radius={120}
					falloff="gaussian"
					className="font-heading italic"
				>
					Ringnalda
				</VariableFontCursorProximity>
			</Link>
		</div>
	)
}

function FooterNewsletter() {
	const fetcher = useFetcher()
	const { t } = useTranslation()
	const isSubmitting = fetcher.state !== 'idle'
	const isSuccess = fetcher.data?.success

	if (isSuccess) {
		return (
			<p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>
				✓ {t('footerSubscribed')}
			</p>
		)
	}

	return (
		<fetcher.Form
			method="POST"
			action="/resources/newsletter-subscribe"
			className="flex gap-2"
		>
			<HoneypotInputs />
			<input type="hidden" name="source" value="footer" />
			<input
				type="email"
				name="email"
				required
				placeholder={t('footerEmailPlaceholder')}
				disabled={isSubmitting}
				className="w-full min-w-0 rounded-full border px-4 py-2 text-[13px] outline-none"
				style={{
					borderColor: 'var(--line)',
					background: 'var(--paper)',
					fontFamily: 'var(--font-sans)',
				}}
			/>
			<button
				type="submit"
				disabled={isSubmitting}
				className="shrink-0 rounded-full px-4 py-2 text-[13px] font-medium transition-colors"
				style={{
					background: 'var(--ink)',
					color: 'var(--paper)',
					opacity: isSubmitting ? 0.6 : 1,
				}}
			>
				{isSubmitting ? '...' : t('footerSubscribeButton')}
			</button>
		</fetcher.Form>
	)
}

function SiteFooter() {
	const { t } = useTranslation()
	return (
		<footer
			className="mt-[120px] grid gap-12 border-t px-[8vw] pb-12 pt-16 md:grid-cols-[2fr_1fr_1.5fr] md:gap-12"
			style={{ borderColor: 'var(--line)' }}
		>
			<div>
				<h4 className="font-heading mb-6 text-[22px] font-medium tracking-tight" style={{ letterSpacing: '-0.01em' }}>
					Else Ringnalda
				</h4>
				<p className="max-w-[380px] text-sm" style={{ color: 'var(--ink-soft)' }}>
					{t('footerStudioDescription')}
				</p>
				<Link to="/contact" className="btn-gallery mt-6 inline-flex text-sm">
					{t('footerContactCta')}
				</Link>
			</div>
			<div>
				<div className="eyebrow mb-3.5">{t('footerVisit')}</div>
				<ul className="flex flex-col gap-2.5 text-[13px]">
					<li><Link to="/beelden" className="transition-colors hover:text-[var(--ink)]" style={{ color: 'var(--ink-soft)' }}>{t('gallery')}</Link></li>
					<li><Link to="/exposities" className="transition-colors hover:text-[var(--ink)]" style={{ color: 'var(--ink-soft)' }}>{t('exhibitions')}</Link></li>
					<li><Link to="/over" className="transition-colors hover:text-[var(--ink)]" style={{ color: 'var(--ink-soft)' }}>{t('about')}</Link></li>
					<li><Link to="/contact" className="transition-colors hover:text-[var(--ink)]" style={{ color: 'var(--ink-soft)' }}>{t('contact')}</Link></li>
				</ul>
			</div>
			<div>
				<div className="eyebrow mb-3.5">{t('footerStayUpdated')}</div>
				<p className="mb-3 text-[13px]" style={{ color: 'var(--ink-soft)' }}>
					{t('footerNewsletterDescription')}
				</p>
				<FooterNewsletter />
			</div>
			<div
				className="flex items-center justify-between border-t pt-8 text-xs md:col-span-3"
				style={{ borderColor: 'var(--paper-3)', color: 'var(--muted-gallery)' }}
			>
				<span>{t('footerCopyright')}</span>
				<span className="flex gap-6">
					<Link to="/privacy" className="hover:text-[var(--ink)]">{t('privacy')}</Link>
					<Link to="/tos" className="hover:text-[var(--ink)]">{t('terms')}</Link>
				</span>
			</div>
		</footer>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
